import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardHeader, Button } from '../../../components/ui';
import { Settings, Plus, Trash2, AlertTriangle, Info, DollarSign, ChevronDown } from 'lucide-react';
import { fetchAPI } from '../../../services/apiClient';

// ── Types ──

interface SorteoInfo {
  sorteo_nombre: string;
  tipo_id: string;
  conceptos: string[];
}

interface VendedorInfo {
  id: string;
  nombre: string;
  role: string;
}

interface ReglaOut {
  id: number;
  local_id: string;
  vendedor_id: string | null;
  vendedor_nombre: string | null;
  sorteo_nombre: string;
  concepto: string;
  paga_local: number;
  activo: boolean;
}

// ── Component ──

export const ReglasPage: React.FC = () => {
  // Data
  const [reglas, setReglas] = useState<ReglaOut[]>([]);
  const [sorteos, setSorteos] = useState<SorteoInfo[]>([]);
  const [vendedores, setVendedores] = useState<VendedorInfo[]>([]);
  const [bancaMults, setBancaMults] = useState<Record<string, Record<string, number>>>({});

  // Form
  const [sorteoNombre, setSorteoNombre] = useState('');
  const [concepto, setConcepto] = useState('');
  const [pagaLocal, setPagaLocal] = useState('');
  const [vendedorId, setVendedorId] = useState('');

  // UI
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // ── Load data ──

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [reglasData, sorteosData, vendedoresData, bancaData] = await Promise.all([
        fetchAPI<ReglaOut[]>('/pago-vendedor/reglas'),
        fetchAPI<SorteoInfo[]>('/pago-vendedor/sorteos'),
        fetchAPI<VendedorInfo[]>('/pago-vendedor/vendedores'),
        fetchAPI<Record<string, Record<string, number>>>('/pago-vendedor/banca-multiplicadores'),
      ]);
      setReglas(reglasData);
      setSorteos(sorteosData);
      setVendedores(vendedoresData);
      setBancaMults(bancaData);
    } catch (e: any) {
      setError(e.message || 'Error cargando datos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Derived ──

  const selectedSorteo = sorteos.find(s => s.sorteo_nombre === sorteoNombre);
  const conceptosDisponibles = selectedSorteo?.conceptos || [];
  const pagaBanca = bancaMults[sorteoNombre]?.[concepto] ?? null;
  const margenEstimado = pagaBanca !== null && pagaLocal !== ''
    ? pagaBanca - Number(pagaLocal)
    : null;

  // ── Save ──

  const handleSave = async () => {
    if (!sorteoNombre || !concepto || !pagaLocal) {
      setError('Complete todos los campos obligatorios');
      return;
    }

    const valor = Number(pagaLocal);
    if (isNaN(valor) || valor <= 0) {
      setError('El monto debe ser mayor a 0');
      return;
    }

    if (pagaBanca !== null && valor > pagaBanca) {
      setError(`El monto (${valor}) no puede ser mayor que lo que paga la banca (${pagaBanca})`);
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await fetchAPI<ReglaOut>('/pago-vendedor/reglas', {
        method: 'POST',
        body: JSON.stringify({
          sorteo_nombre: sorteoNombre,
          concepto,
          paga_local: valor,
          vendedor_id: vendedorId || null,
        }),
      });
      setSuccess('Regla guardada');
      setPagaLocal('');
      setConcepto('');
      await loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e: any) {
      setError(e.message || 'Error guardando regla');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('¿Desactivar esta regla?')) return;
    try {
      await fetchAPI(`/pago-vendedor/reglas/${id}`, { method: 'DELETE' });
      setSuccess('Regla desactivada');
      await loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e: any) {
      setError(e.message || 'Error eliminando regla');
    }
  };

  // ── Render ──

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Reglas de Pago a Vendedores</h1>
        <p className="text-gray-500 mt-1">
          Defina cuánto paga el local al vendedor por cada tipo de premio
        </p>
      </div>

      {/* Warning banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3">
        <Info className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
        <div>
          <p className="text-amber-900 font-medium text-sm">Estas reglas NO afectan lo que paga la banca ni los saldos</p>
          <p className="text-amber-800 text-xs mt-1">
            Solo afectan el monto mostrado al cliente y los reportes de ganancia del local.
            El saldo SIEMPRE se calcula con lo que paga la BANCA.
          </p>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex gap-2 items-center">
          <AlertTriangle className="text-red-500 flex-shrink-0" size={16} />
          <span className="text-red-700 text-sm">{error}</span>
          <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-600">&times;</button>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-green-700 text-sm">
          {success}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Form */}
        <Card>
          <CardHeader title="Nueva Regla" subtitle="Agregar o actualizar una regla de pago" />
          <div className="space-y-4 mt-4">
            {/* Sorteo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sorteo *</label>
              <div className="relative">
                <select
                  className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 pr-8 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 appearance-none"
                  value={sorteoNombre}
                  onChange={e => {
                    setSorteoNombre(e.target.value);
                    setConcepto('');
                    setPagaLocal('');
                  }}
                >
                  <option value="">Seleccionar sorteo</option>
                  {sorteos.map(s => (
                    <option key={s.sorteo_nombre} value={s.sorteo_nombre}>
                      {s.sorteo_nombre} ({s.tipo_id})
                    </option>
                  ))}
                </select>
                <ChevronDown size={16} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Concepto */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Concepto *</label>
              <div className="relative">
                <select
                  className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 pr-8 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 appearance-none disabled:bg-gray-100"
                  value={concepto}
                  onChange={e => {
                    setConcepto(e.target.value);
                    setPagaLocal('');
                  }}
                  disabled={!sorteoNombre}
                >
                  <option value="">Seleccionar concepto</option>
                  {conceptosDisponibles.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <ChevronDown size={16} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Vendedor */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vendedor <span className="text-gray-400 text-xs">(opcional — vacío = todos)</span>
              </label>
              <div className="relative">
                <select
                  className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 pr-8 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 appearance-none"
                  value={vendedorId}
                  onChange={e => setVendedorId(e.target.value)}
                >
                  <option value="">Todos los vendedores</option>
                  {vendedores.map(v => (
                    <option key={v.id} value={v.id}>{v.nombre}</option>
                  ))}
                </select>
                <ChevronDown size={16} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Paga Local */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Monto que paga el local *
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">₡</span>
                <input
                  type="number"
                  min="1"
                  step="1"
                  className="block w-full rounded-lg border border-gray-300 bg-white pl-7 pr-3 py-2.5 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  placeholder="Ej: 85"
                  value={pagaLocal}
                  onChange={e => setPagaLocal(e.target.value)}
                />
              </div>

              {/* Paga banca + margen info */}
              {pagaBanca !== null && (
                <div className="mt-2 space-y-1">
                  <p className="text-xs text-gray-500">
                    Banca paga: <span className="font-semibold text-gray-700">₡{pagaBanca.toLocaleString()}</span>
                  </p>
                  {margenEstimado !== null && (
                    <p className={`text-xs font-medium ${margenEstimado >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      Ganancia estimada del local: ₡{margenEstimado.toLocaleString()} por jugada ganadora
                    </p>
                  )}
                  {Number(pagaLocal) > pagaBanca && (
                    <p className="text-xs text-red-600 flex items-center gap-1">
                      <AlertTriangle size={12} />
                      No puede ser mayor que lo que paga la banca
                    </p>
                  )}
                </div>
              )}
            </div>

            <Button
              variant="primary"
              className="w-full"
              leftIcon={<Plus size={18} />}
              onClick={handleSave}
              disabled={saving || !sorteoNombre || !concepto || !pagaLocal}
            >
              {saving ? 'Guardando...' : 'Guardar Regla'}
            </Button>
          </div>
        </Card>

        {/* Current rules table */}
        <Card>
          <CardHeader title="Reglas Activas" subtitle={`${reglas.length} reglas configuradas`} />
          <div className="mt-4">
            {reglas.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Settings size={40} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">No hay reglas configuradas</p>
                <p className="text-xs mt-1">Se usarán los multiplicadores de la banca</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-gray-500 text-xs uppercase">
                      <th className="pb-2 pr-2">Sorteo</th>
                      <th className="pb-2 pr-2">Concepto</th>
                      <th className="pb-2 pr-2">Vendedor</th>
                      <th className="pb-2 pr-2 text-right">Paga Local</th>
                      <th className="pb-2 pr-2 text-right">Paga Banca</th>
                      <th className="pb-2 pr-2 text-right">Margen</th>
                      <th className="pb-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {reglas.map(r => {
                      const pb = bancaMults[r.sorteo_nombre]?.[r.concepto] ?? null;
                      const margen = pb !== null ? pb - r.paga_local : null;
                      return (
                        <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-2 pr-2 font-medium text-gray-900">{r.sorteo_nombre}</td>
                          <td className="py-2 pr-2 text-gray-600">{r.concepto}</td>
                          <td className="py-2 pr-2 text-gray-600">
                            {r.vendedor_nombre || 'Todos'}
                          </td>
                          <td className="py-2 pr-2 text-right font-semibold text-gray-900">
                            ₡{Number(r.paga_local).toLocaleString()}
                          </td>
                          <td className="py-2 pr-2 text-right text-gray-500">
                            {pb !== null ? `₡${pb.toLocaleString()}` : '—'}
                          </td>
                          <td className={`py-2 pr-2 text-right font-medium ${margen !== null && margen >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {margen !== null ? `₡${margen.toLocaleString()}` : '—'}
                          </td>
                          <td className="py-2">
                            <button
                              onClick={() => handleDelete(r.id)}
                              className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                              title="Desactivar regla"
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Summary by sorteo */}
      {Object.keys(bancaMults).length > 0 && (
        <Card>
          <CardHeader title="Referencia: Multiplicadores de Banca" subtitle="Lo que la banca paga por cada concepto" />
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-gray-500 text-xs uppercase">
                  <th className="pb-2 pr-4">Sorteo</th>
                  <th className="pb-2 pr-4">Concepto</th>
                  <th className="pb-2 text-right">Multiplicador Banca</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(bancaMults).flatMap(([sorteo, conceptos]) =>
                  Object.entries(conceptos).map(([conc, mult]) => (
                    <tr key={`${sorteo}-${conc}`} className="border-b border-gray-100">
                      <td className="py-1.5 pr-4 text-gray-900">{sorteo}</td>
                      <td className="py-1.5 pr-4 text-gray-600">{conc}</td>
                      <td className="py-1.5 text-right font-medium text-gray-900">×{mult}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};

export default ReglasPage;
